<script setup>
import DefaultLayout from '@frontJs/layouts/DefaultLayout.vue';
import BellRingingIcon from '@iconify-vue/ph/bell-ringing-fill';
import BuildingOfficeIcon from '@iconify-vue/ph/building-office-fill';
import CodeIcon from '@iconify-vue/ph/code-fill';
import CursorClickIcon from '@iconify-vue/ph/cursor-click-fill';
import FileTextIcon from '@iconify-vue/ph/file-text-fill';
import ListChecksIcon from '@iconify-vue/ph/list-checks-fill';
import MegaphoneIcon from '@iconify-vue/ph/megaphone-fill';
import PuzzlePieceIcon from '@iconify-vue/ph/puzzle-piece-fill';
import SparkleIcon from '@iconify-vue/ph/sparkle-fill';
import TargetIcon from '@iconify-vue/ph/target-fill';
import UsersIcon from '@iconify-vue/ph/users-fill';
import GoogleAdsIcon from '@iconify-vue/simple-icons/googleads';
import MetaIcon from '@iconify-vue/simple-icons/meta';
import NavCollapsible from '@js/components/navigation/NavCollapsible.vue';
import NavGroup from '@js/components/navigation/NavGroup.vue';
import NavItem from '@js/components/navigation/NavItem.vue';
import { hasRole } from '@js/utils/permission';
import { useRoute } from '@js/utils/route';
import { ref } from 'vue';

const { route, routesAreActive } = useRoute();

const showCustomSidebar = ref(true);

function toggleCustomSidebar() {
    showCustomSidebar.value = !showCustomSidebar.value;
}
</script>

<template>
    <DefaultLayout
        :show-custom-sidebar="showCustomSidebar"
        :toggle-custom-sidebar="toggleCustomSidebar"
    >
        <NavGroup
            v-slot="{ setCurrentDisclosure }"
            v-if="hasRole('super_admin') || hasRole('admin')"
            class="fixed top-[4.05rem] left-0 z-50 h-[calc(100%-4.05rem)] w-64 overflow-y-auto border-r border-gray-200 bg-indigo-950 px-3 py-4 text-white"
            :class="{
                hidden: !showCustomSidebar,
                'hidden xl:flex': showCustomSidebar,
            }"
        >
            <NavCollapsible
                v-slot="{ disclosure }"
                title="Tâches"
                :icon="ListChecksIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.task.objectives',
                        'front.projectManagement.task.tasks',
                        'front.projectManagement.task.projects',
                        'front.projectManagement.task.createTask',
                        'front.projectManagement.task.createTaskIdea',
                        'front.projectManagement.task.updateTask',
                        'front.projectManagement.task.updateTaskIdea',
                        'front.projectManagement.task.taskTimes',
                        'front.projectManagement.task.tickets',
                        'front.projectManagement.task.ticket',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
                always-open
            >
                <NavItem
                    title="Tâches Apikom"
                    :href="route('front.projectManagement.task.tasks')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.tasks',
                            'front.projectManagement.task.createTaskIdea',
                            'front.projectManagement.task.createTask',
                            'front.projectManagement.task.updateTask',
                            'front.projectManagement.task.updateTaskIdea',
                        ])
                    "
                    :level="1"
                    :disclosure="disclosure"
                    :setCurrentDisclosure="setCurrentDisclosure"
                />

                <NavItem
                    title="Projets Apikom"
                    :href="route('front.projectManagement.task.projects')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.projects',
                        ])
                    "
                    :level="1"
                    :disclosure="disclosure"
                    :setCurrentDisclosure="setCurrentDisclosure"
                />

                <NavItem
                    title="Temps Apikom"
                    :href="route('front.projectManagement.task.taskTimes')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.taskTimes',
                        ])
                    "
                    :level="1"
                    :disclosure="disclosure"
                    :setCurrentDisclosure="setCurrentDisclosure"
                />

                <NavItem
                    title="Tickets Apikom"
                    :href="route('front.projectManagement.task.tickets')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.tickets',
                            'front.projectManagement.task.ticket',
                        ])
                    "
                    :level="1"
                    :disclosure="disclosure"
                    :setCurrentDisclosure="setCurrentDisclosure"
                />

                <NavItem
                    title="Objectifs"
                    :href="route('front.projectManagement.task.objectives')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.task.objectives',
                        ])
                    "
                    :level="1"
                    :disclosure="disclosure"
                    :setCurrentDisclosure="setCurrentDisclosure"
                />
            </NavCollapsible>

            <NavCollapsible
                title="IA"
                :icon="SparkleIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.ai.productContents',
                        'front.projectManagement.ai.productImages',
                        'front.projectManagement.ai.productImageAttachment',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Contenus produits"
                    :href="route('front.projectManagement.ai.productContents')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.ai.productContents',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Images produits"
                    :href="route('front.projectManagement.ai.productImages')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.ai.productImages',
                            'front.projectManagement.ai.productImageAttachment',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="Apikom"
                :icon="BuildingOfficeIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.apikom.newsArticles',
                        'front.projectManagement.apikom.newsArticle',
                        'front.projectManagement.apikom.newsTheme',
                        'front.projectManagement.apikom.affiliations',
                        'front.projectManagement.apikom.createAffiliation',
                        'front.projectManagement.apikom.updateAffiliation',
                        'front.projectManagement.apikom.phoneCalls',
                        'front.projectManagement.apikom.quotations',
                        'front.projectManagement.apikom.createQuotation',
                        'front.projectManagement.apikom.updateQuotation',
                        'front.projectManagement.apikom.invoicing',
                        'front.projectManagement.apikom.packages',
                        'front.projectManagement.apikom.package',
                        'front.projectManagement.apikom.opportunities',
                        'front.projectManagement.apikom.partners',
                        'front.projectManagement.apikom.createPartner',
                        'front.projectManagement.apikom.updatePartner',
                        'front.projectManagement.apikom.prospects',
                        'front.projectManagement.apikom.prospect',
                        'front.projectManagement.apikom.appointments',
                        'front.projectManagement.apikom.createAppointment',
                        'front.projectManagement.apikom.updateAppointment',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Actualités"
                    :href="route('front.projectManagement.apikom.newsArticles')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.newsArticles',
                            'front.projectManagement.apikom.newsArticle',
                            'front.projectManagement.apikom.newsTheme',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Affiliations"
                    :href="route('front.projectManagement.apikom.affiliations')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.affiliations',
                            'front.projectManagement.apikom.createAffiliation',
                            'front.projectManagement.apikom.updateAffiliation',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Appels"
                    :href="route('front.projectManagement.apikom.phoneCalls')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.phoneCalls',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Devis"
                    :href="route('front.projectManagement.apikom.quotations')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.quotations',
                            'front.projectManagement.apikom.createQuotation',
                            'front.projectManagement.apikom.updateQuotation',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Facturation"
                    :href="route('front.projectManagement.apikom.invoicing')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.invoicing',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Forfaits"
                    :href="route('front.projectManagement.apikom.packages')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.packages',
                            'front.projectManagement.apikom.package',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Opportunités"
                    :href="
                        route('front.projectManagement.apikom.opportunities')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.opportunities',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Partenaires"
                    :href="route('front.projectManagement.apikom.partners')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.partners',
                            'front.projectManagement.apikom.createPartner',
                            'front.projectManagement.apikom.updatePartner',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Prospects"
                    :href="route('front.projectManagement.apikom.prospects')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.prospects',
                            'front.projectManagement.apikom.prospect',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Rendez-vous"
                    :href="route('front.projectManagement.apikom.appointments')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.apikom.appointments',
                            'front.projectManagement.apikom.createAppointment',
                            'front.projectManagement.apikom.updateAppointment',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="Clients"
                :icon="UsersIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.customer.budgets',
                        'front.projectManagement.customer.contacts',
                        'front.projectManagement.customer.createCustomer',
                        'front.projectManagement.customer.updateCustomer',
                        'front.projectManagement.customer.externalServices',
                        'front.projectManagement.customer.externalServices.googleMerchant',
                        'front.projectManagement.customer.externalServices.facebook',
                        'front.projectManagement.customer.externalServices.facebookAds',
                        'front.projectManagement.customer.externalServices.instagram',
                        'front.projectManagement.customer.externalServices.hiboutik',
                        'front.projectManagement.customer.externalServices.amazon',
                        'front.projectManagement.customer.externalServices.manoMano',
                        'front.projectManagement.customer.externalServices.eCommerce',
                        'front.projectManagement.customer.externalServices.verifiedReviews',
                        'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                        'front.projectManagement.customer.externalServices.mailjet',
                        'front.projectManagement.customer.externalServices.printNode',
                        'front.projectManagement.customer.externalServices.googleMyBusiness',
                        'front.projectManagement.customer.externalServices.googleAnalytics',
                        'front.projectManagement.customer.externalServices.googleMyBusiness',
                        'front.projectManagement.customer.externalServices.googleSearchConsole',
                        'front.projectManagement.customer.externalServices.googleAds',
                        'front.projectManagement.customer.services',
                        'front.projectManagement.customer.reports',
                        'front.projectManagement.customer.websiteVariants',
                        'front.projectManagement.customer.websiteVariant',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Budgets"
                    :href="route('front.projectManagement.customer.budgets')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.budgets',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Contacts"
                    :href="route('front.projectManagement.customer.contacts')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.contacts',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Prestations"
                    :href="route('front.projectManagement.customer.services')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.services',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Rapports"
                    :href="route('front.projectManagement.customer.reports')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.reports',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Sites"
                    :href="
                        route(
                            'front.projectManagement.customer.websiteVariants',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.websiteVariants',
                            'front.projectManagement.customer.websiteVariant',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Services externes"
                    :href="
                        route(
                            'front.projectManagement.customer.externalServices',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.customer.externalServices',
                            'front.projectManagement.customer.externalServices.googleMerchant',
                            'front.projectManagement.customer.externalServices.facebook',
                            'front.projectManagement.customer.externalServices.facebookAds',
                            'front.projectManagement.customer.externalServices.instagram',
                            'front.projectManagement.customer.externalServices.hiboutik',
                            'front.projectManagement.customer.externalServices.amazon',
                            'front.projectManagement.customer.externalServices.manoMano',
                            'front.projectManagement.customer.externalServices.eCommerce',
                            'front.projectManagement.customer.externalServices.verifiedReviews',
                            'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                            'front.projectManagement.customer.externalServices.mailjet',
                            'front.projectManagement.customer.externalServices.printNode',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleAnalytics',
                            'front.projectManagement.customer.externalServices.googleMyBusiness',
                            'front.projectManagement.customer.externalServices.googleSearchConsole',
                            'front.projectManagement.customer.externalServices.googleAds',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="DEV"
                :icon="CodeIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.dev.eCommerceModules',
                        'front.projectManagement.dev.pageSpeed',
                        'front.projectManagement.dev.createPageSpeedUrl',
                        'front.projectManagement.dev.servers',
                        'front.projectManagement.dev.websites',
                        'front.projectManagement.dev.website',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Modules e-commerce"
                    :href="
                        route('front.projectManagement.dev.eCommerceModules')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.dev.eCommerceModules',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Sécurité"
                    href="https://monitoring.apikom.fr/login"
                    :external="true"
                    :level="1"
                />

                <NavItem
                    title="Serveurs"
                    :href="route('front.projectManagement.dev.servers')"
                    :active="
                        routesAreActive(['front.projectManagement.dev.servers'])
                    "
                    :level="1"
                />

                <NavItem
                    title="Sites web"
                    :href="route('front.projectManagement.dev.websites')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.dev.websites',
                            'front.projectManagement.dev.website',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Temps de chargement"
                    :href="route('front.projectManagement.dev.pageSpeed')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.dev.pageSpeed',
                            'front.projectManagement.dev.createPageSpeedUrl',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="SEA"
                :icon="GoogleAdsIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.sea.audiences',
                        'front.projectManagement.sea.audience',
                        'front.projectManagement.sea.planning',
                        'front.projectManagement.sea.planningKeywords',
                        'front.projectManagement.sea.planningKeyword',
                        'front.projectManagement.sea.productManagements',
                        'front.projectManagement.sea.productManagement',
                        'front.projectManagement.sea.keywordManagements',
                        'front.projectManagement.sea.keywordManagement',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Audiences"
                    :href="route('front.projectManagement.sea.audiences')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sea.audiences',
                            'front.projectManagement.sea.audience',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Pilotage Search"
                    :href="
                        route('front.projectManagement.sea.keywordManagements')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.sea.keywordManagements',
                            'front.projectManagement.sea.keywordManagement',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Pilotage Shopping"
                    :href="
                        route('front.projectManagement.sea.productManagements')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.sea.productManagements',
                            'front.projectManagement.sea.productManagement',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Planification"
                    :href="route('front.projectManagement.sea.planning')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sea.planning',
                            'front.projectManagement.sea.planningKeywords',
                            'front.projectManagement.sea.planningKeyword',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="SMA"
                :icon="MetaIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.sma.audiences',
                        'front.projectManagement.sma.audience',
                        'front.projectManagement.sma.productFeeds',
                        'front.projectManagement.sma.productFeed',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Audiences"
                    :href="route('front.projectManagement.sma.audiences')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sma.audiences',
                            'front.projectManagement.sma.audience',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Flux produits"
                    :href="route('front.projectManagement.sma.productFeeds')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sma.productFeeds',
                            'front.projectManagement.sma.productFeed',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="SMO"
                :icon="MegaphoneIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.smo.posts',
                        'front.projectManagement.smo.post',
                        'front.projectManagement.smo.batchPosts',
                        'front.projectManagement.smo.calendar',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Publications"
                    :href="route('front.projectManagement.smo.posts')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.smo.posts',
                            'front.projectManagement.smo.post',
                            'front.projectManagement.smo.batchPosts',
                            'front.projectManagement.smo.calendar',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="SEO/GEO"
                :icon="CursorClickIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.seo.apikomContents',
                        'front.projectManagement.seo.createApikomContent',
                        'front.projectManagement.seo.updateApikomContent',
                        'front.projectManagement.seo.eCommerceCategoryContents',
                        'front.projectManagement.seo.eCommerceProductContents',
                        'front.projectManagement.seo.devSeo',
                        'front.projectManagement.seo.indexation',
                        'front.projectManagement.seo.netlinking',
                        'front.projectManagement.seo.backlink',
                        'front.projectManagement.seo.positionTracking',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Contenus Apikom"
                    :href="route('front.projectManagement.seo.apikomContents')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.apikomContents',
                            'front.projectManagement.seo.createApikomContent',
                            'front.projectManagement.seo.updateApikomContent',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Contenus catégories"
                    :href="
                        route(
                            'front.projectManagement.seo.eCommerceCategoryContents',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.eCommerceCategoryContents',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Contenus produits"
                    :href="
                        route(
                            'front.projectManagement.seo.eCommerceProductContents',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.eCommerceProductContents',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="DEV SEO"
                    :href="route('front.projectManagement.seo.devSeo')"
                    :active="
                        routesAreActive(['front.projectManagement.seo.devSeo'])
                    "
                    :level="1"
                />

                <NavItem
                    title="Indexation"
                    :href="route('front.projectManagement.seo.indexation')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.indexation',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Netlinking"
                    :href="route('front.projectManagement.seo.netlinking')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.netlinking',
                            'front.projectManagement.seo.backlink',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Suivi positions / URL"
                    :href="
                        route('front.projectManagement.seo.positionTracking')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.seo.positionTracking',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="SEM"
                :icon="TargetIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.sem.banners',
                        'front.projectManagement.sem.banner',
                        'front.projectManagement.sem.emailings',
                        'front.projectManagement.sem.emailing',
                        'front.projectManagement.sem.shortMessageServices',
                        'front.projectManagement.sem.shortMessageService',
                        'front.projectManagement.sem.events',
                        'front.projectManagement.sem.createEvent',
                        'front.projectManagement.sem.event',
                        'front.projectManagement.sem.customerAudits',
                        'front.projectManagement.sem.customerAudit',
                        'front.projectManagement.sem.segmentations',
                        'front.projectManagement.sem.segmentation',
                        'front.projectManagement.sem.segmentationCustomers',
                        'front.projectManagement.sem.lifeTimeValues',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Audits clients"
                    :href="route('front.projectManagement.sem.customerAudits')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.customerAudits',
                            'front.projectManagement.sem.customerAudit',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Bannières"
                    :href="route('front.projectManagement.sem.banners')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.banners',
                            'front.projectManagement.sem.banner',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Emailings"
                    :href="route('front.projectManagement.sem.emailings')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.emailings',
                            'front.projectManagement.sem.emailing',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Événements"
                    :href="route('front.projectManagement.sem.events')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.events',
                            'front.projectManagement.sem.createEvent',
                            'front.projectManagement.sem.event',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="LTV"
                    :href="route('front.projectManagement.sem.lifeTimeValues')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.lifeTimeValues',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Segmentations"
                    :href="route('front.projectManagement.sem.segmentations')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.segmentations',
                            'front.projectManagement.sem.segmentation',
                            'front.projectManagement.sem.segmentationCustomers',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="SMS"
                    :href="
                        route(
                            'front.projectManagement.sem.shortMessageServices',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.sem.shortMessageServices',
                            'front.projectManagement.sem.shortMessageService',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavCollapsible
                title="Modules"
                :icon="PuzzlePieceIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.modules.emailingAutomations',
                        'front.projectManagement.modules.emailingAutomation',
                        'front.projectManagement.modules.emailingAutomationVariable',
                        'front.projectManagement.modules.emailingAutomationPromoPriceRule',
                        'front.projectManagement.modules.competitors',
                        'front.projectManagement.modules.competitor',
                        'front.projectManagement.modules.competitorReport',
                        'front.projectManagement.modules.modulePackages',
                        'front.projectManagement.modules.modulePackage',
                        'front.projectManagement.modules.moduleInvoicing',
                        'front.projectManagement.modules.emailings',
                        'front.projectManagement.modules.emailing',
                        'front.projectManagement.modules.emailingVariable',
                    ])
                "
                :level="0"
                :set-current-disclosure="setCurrentDisclosure"
            >
                <NavItem
                    title="Concurrents"
                    :href="route('front.projectManagement.modules.competitors')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.modules.competitors',
                            'front.projectManagement.modules.competitor',
                            'front.projectManagement.modules.competitorReport',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Emailings"
                    :href="route('front.projectManagement.modules.emailings')"
                    :active="
                        routesAreActive([
                            'front.projectManagement.modules.emailings',
                            'front.projectManagement.modules.emailing',
                            'front.projectManagement.modules.emailingVariable',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Emailings automatisés"
                    :href="
                        route(
                            'front.projectManagement.modules.emailingAutomations',
                        )
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.modules.emailingAutomations',
                            'front.projectManagement.modules.emailingAutomation',
                            'front.projectManagement.modules.emailingAutomationVariable',
                            'front.projectManagement.modules.emailingAutomationPromoPriceRule',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Facturation modules"
                    :href="
                        route('front.projectManagement.modules.moduleInvoicing')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.modules.moduleInvoicing',
                        ])
                    "
                    :level="1"
                />

                <NavItem
                    title="Forfaits modules"
                    :href="
                        route('front.projectManagement.modules.modulePackages')
                    "
                    :active="
                        routesAreActive([
                            'front.projectManagement.modules.modulePackages',
                            'front.projectManagement.modules.modulePackage',
                        ])
                    "
                    :level="1"
                />
            </NavCollapsible>

            <NavItem
                title="Alertes"
                :href="route('front.projectManagement.alerts')"
                :icon="BellRingingIcon"
                :active="routesAreActive(['front.projectManagement.alerts'])"
                :level="0"
            />

            <NavItem
                :title="'Doc & FAQ'"
                :href="route('front.projectManagement.docAndFaq')"
                :icon="FileTextIcon"
                :active="
                    routesAreActive([
                        'front.projectManagement.docAndFaq',
                        'front.projectManagement.document',
                        'front.projectManagement.faqs',
                        'front.projectManagement.faq',
                    ])
                "
                :level="0"
            />
        </NavGroup>

        <div :class="{ 'xl:ml-64': showCustomSidebar }">
            <slot />
        </div>
    </DefaultLayout>
</template>
